import type { StoryDetailDto, StorySaveRequest } from "Infrastructure/Api/Api";
import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

const VALIDATION_RESOURCES = Resources.Stories.PostStory.Validation;

// Length caps mirror the server-side StoryContentValidator (sized from the Story entity).
export const MAX_TEXT_LENGTH = 500;
const MAX_BUTTON_LABEL_LENGTH = 50;
const MAX_SHEET_TITLE_LENGTH = 100;
const MAX_SHEET_DESCRIPTION_LENGTH = 1000;
const MAX_SHEET_PRICE_LENGTH = 50;
const MAX_URL_LENGTH = 2048;

// The CTA link renders as a plain anchor in every viewer — https only, like the backend.
const isEmptyOrHttpsUrl = (value: string) => {
  if (value.trim() === "") {
    return true;
  }
  try {
    const url = new URL(value);
    return url.protocol === "https:";
  } catch {
    return false;
  }
};

/** Everything that lives in the offer sheet: its fields plus the link on its action button. */
export const OFFER_FIELDS = [
  "sheetTitle",
  "sheetDescription",
  "sheetPrice",
  "sheetPriceNote",
  "sheetImageUrl",
  "buttonUrl",
] as const;

const hasOfferContent = (data: Record<(typeof OFFER_FIELDS)[number], string>) =>
  OFFER_FIELDS.some((field) => data[field].trim() !== "");

export const getStoryFormSchema = (t: TFunction) =>
  z
    .object({
      imageUrl: z.string().max(MAX_URL_LENGTH),
      videoUrl: z.string().max(MAX_URL_LENGTH),
      text: z.string().max(MAX_TEXT_LENGTH),
      buttonLabel: z.string().max(MAX_BUTTON_LABEL_LENGTH),
      buttonUrl: z
        .string()
        .max(MAX_URL_LENGTH)
        .refine(isEmptyOrHttpsUrl, t(VALIDATION_RESOURCES.ButtonUrlInvalid)),
      sheetTitle: z.string().max(MAX_SHEET_TITLE_LENGTH),
      sheetDescription: z.string().max(MAX_SHEET_DESCRIPTION_LENGTH),
      sheetPrice: z.string().max(MAX_SHEET_PRICE_LENGTH),
      sheetPriceNote: z.string().max(MAX_SHEET_PRICE_LENGTH),
      sheetImageUrl: z.string().max(MAX_URL_LENGTH),
    })
    .superRefine((data, ctx) => {
      // A story needs at least one medium; with both, the image doubles as the video poster.
      if (!data.imageUrl && !data.videoUrl) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["imageUrl"],
          message: t(VALIDATION_RESOURCES.MediaRequired),
        });
      }
      // The viewer only renders the offer sheet behind the story button (StoryViewer gates on
      // buttonLabel), so an offer without a label would be saved but never shown.
      if (!data.buttonLabel.trim() && hasOfferContent(data)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["buttonLabel"],
          message: t(VALIDATION_RESOURCES.ButtonLabelRequired),
        });
      }
    });

export type StoryFormData = z.infer<ReturnType<typeof getStoryFormSchema>>;

export const EMPTY_STORY_FORM: StoryFormData = {
  imageUrl: "",
  videoUrl: "",
  text: "",
  buttonLabel: "",
  buttonUrl: "",
  sheetTitle: "",
  sheetDescription: "",
  sheetPrice: "",
  sheetPriceNote: "",
  sheetImageUrl: "",
};

const orUndefined = (value: string) =>
  value.trim() === "" ? undefined : value;

export const toStorySaveRequest = (data: StoryFormData): StorySaveRequest => ({
  imageUrl: orUndefined(data.imageUrl),
  videoUrl: orUndefined(data.videoUrl),
  text: orUndefined(data.text),
  buttonLabel: orUndefined(data.buttonLabel),
  buttonUrl: orUndefined(data.buttonUrl),
  sheetTitle: orUndefined(data.sheetTitle),
  sheetDescription: orUndefined(data.sheetDescription),
  sheetPrice: orUndefined(data.sheetPrice),
  sheetPriceNote: orUndefined(data.sheetPriceNote),
  sheetImageUrl: orUndefined(data.sheetImageUrl),
});

export const toStoryFormData = (detail: StoryDetailDto): StoryFormData => ({
  imageUrl: detail.summary.imageUrl ?? "",
  videoUrl: detail.summary.videoUrl ?? "",
  text: detail.summary.text ?? "",
  buttonLabel: detail.buttonLabel ?? "",
  buttonUrl: detail.buttonUrl ?? "",
  sheetTitle: detail.sheetTitle ?? "",
  sheetDescription: detail.sheetDescription ?? "",
  sheetPrice: detail.sheetPrice ?? "",
  sheetPriceNote: detail.sheetPriceNote ?? "",
  sheetImageUrl: detail.sheetImageUrl ?? "",
});
